import type { QueryClient } from '@tanstack/react-query';
import type { LeagueSummary } from '@/types/league';
import { trpc, type RouterOutputs } from './trpc';

/**
 * Store the given league payload under both the season-scoped and legacy cache keys.
 * This lets season-aware screens fetch `/api/leagues/:id?seasonId=...` while older
 * callers can still read from `/api/leagues/:id`.
 */
export function primeLeagueCache(
  client: QueryClient,
  league: LeagueSummary | null | undefined,
) {
  if (!league?.id) {
    return;
  }

  const leagueId = String(league.id);
  const seasonKey = league.resolvedSeasonId ?? 'active';
  const trpcLeague = {
    ...league,
    id: leagueId,
  } as unknown as RouterOutputs['leagues']['detail'];

  // League detail reads use tRPC query keys now. Keep the legacy keys below for
  // older REST-backed callers, but prime the keys used by useLeague first.
  client.setQueryData(
    trpc.leagues.detail.queryKey({ leagueId, seasonId: league.resolvedSeasonId }),
    trpcLeague,
  );
  client.setQueryData(trpc.leagues.detail.queryKey({ leagueId }), trpcLeague);

  client.setQueryData(['/api/leagues', leagueId, seasonKey], league);
  client.setQueryData(['/api/leagues', leagueId], league);
}
